import { CheckCircle2, GitCommitHorizontal, Play, XCircle } from 'lucide-react';
import {
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@design-system';
import type {
  WizardCalcSnapshotRun,
  WizardCalcSnapshotTestCase,
} from '../api/wizard-calc-snapshots-api';
import {
  CalcSnapshotMetricCard,
  ResultBadge,
  formatCalcSnapshotGitRevision,
  formatSnapshotDateTime,
} from './CalcSnapshotUi';

interface CalcSnapshotRunSheetProps {
  run: WizardCalcSnapshotRun | null;
  testCases: WizardCalcSnapshotTestCase[];
  onReviewResult: (testCaseId: string, runResultId: string) => void;
  onOpenChange: (open: boolean) => void;
}

export function CalcSnapshotRunSheet({
  run,
  testCases,
  onReviewResult,
  onOpenChange,
}: CalcSnapshotRunSheetProps) {
  const testCaseById = new Map(testCases.map(testCase => [testCase.testCaseId, testCase]));
  const warningTestCaseCount = run?.results.filter(result => result.warningCount > 0).length ?? 0;

  return (
    <Sheet
      open={run != null}
      onOpenChange={onOpenChange}
    >
      <SheetContent className="w-full gap-0 overflow-hidden p-0 sm:w-[85vw] sm:max-w-[1080px]">
        {run && (
          <>
            <SheetHeader className="border-b px-6 py-5 pr-14 text-left">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <RunStatusBadge run={run} />
                <Badge variant="outline">{scopeLabel(run.scope)}</Badge>
              </div>
              <SheetTitle className="text-xl">Calculation test run</SheetTitle>
              <SheetDescription>
                {formatSnapshotDateTime(run.createdDateTime)} · triggered by {run.triggeredBy}
              </SheetDescription>
            </SheetHeader>

            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <CalcSnapshotMetricCard
                  label="Test cases"
                  value={run.aggregate.testCaseCount}
                  valueClassName="text-2xl"
                />
                <CalcSnapshotMetricCard
                  label="Passed"
                  value={run.aggregate.passedCount}
                  tone="success"
                  valueClassName="text-2xl"
                />
                <CalcSnapshotMetricCard
                  label="Failed"
                  value={run.aggregate.failedCount}
                  tone={run.aggregate.failedCount ? 'danger' : 'neutral'}
                  valueClassName="text-2xl"
                />
                <CalcSnapshotMetricCard
                  label="Warnings"
                  value={warningTestCaseCount}
                  tone={warningTestCaseCount ? 'warning' : 'neutral'}
                  valueClassName="text-2xl"
                />
              </div>

              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 rounded-xl border bg-gray-50 px-4 py-3 text-xs text-gray-600">
                <span className="flex items-center gap-2">
                  <GitCommitHorizontal className="size-4" />
                  {run.gitSha
                    ? `Commit ${formatCalcSnapshotGitRevision(run.gitSha)}`
                    : 'Local working tree'}
                </span>
                <span>Started {formatSnapshotDateTime(run.startedAt)}</span>
                {run.completedAt && (
                  <span>Completed {formatSnapshotDateTime(run.completedAt)}</span>
                )}
              </div>

              <section className="mt-6 overflow-hidden rounded-xl border">
                <div className="border-b bg-gray-50 px-4 py-3">
                  <h3 className="font-semibold text-gray-950">Test case results</h3>
                </div>
                <div className="text-muted-foreground hidden grid-cols-[1.2fr_0.7fr_auto] gap-3 border-b px-8 py-3 text-xs font-medium md:grid">
                  <span>Test case</span>
                  <span>Status</span>
                  <span className="text-right">Action</span>
                </div>
                {run.results.map(result => {
                  const testCase = testCaseById.get(result.testCaseId);
                  return (
                    <div
                      key={result.runResultId}
                      className="grid grid-cols-1 gap-3 border-b px-8 py-4 last:border-b-0 md:grid-cols-[1.2fr_0.7fr_auto] md:items-center"
                    >
                      <div>
                        <p className="font-medium text-gray-950">
                          {testCase?.name ?? result.testCaseId}
                        </p>
                        {testCase && (
                          <p className="text-muted-foreground mt-0.5 text-xs">
                            {testCase.strategyId} · revision {result.testCaseRevision}
                          </p>
                        )}
                      </div>
                      <div>
                        <ResultBadge result={result} />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="justify-self-start md:justify-self-end"
                        onClick={() => onReviewResult(result.testCaseId, result.runResultId)}
                      >
                        Review
                      </Button>
                    </div>
                  );
                })}
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function RunStatusBadge({ run }: { run: WizardCalcSnapshotRun }) {
  if (run.status === 'running') {
    return (
      <Badge variant="warning">
        <Play /> Running
      </Badge>
    );
  }
  if (run.status === 'failed' || run.aggregate.failedCount > 0) {
    return (
      <Badge variant="destructive">
        <XCircle /> Failed
      </Badge>
    );
  }
  return (
    <Badge variant={run.aggregate.warningCount ? 'warning' : 'success'}>
      <CheckCircle2 /> {run.aggregate.warningCount ? 'Passed with warnings' : 'Passed'}
    </Badge>
  );
}

function scopeLabel(scope: WizardCalcSnapshotRun['scope']): string {
  if (scope === 'all') return 'All test cases';
  if (scope === 'tag') return 'Tag';
  return 'Single test case';
}
